import { useEffect, useRef, useState } from 'react';
import type { ScheduleDay } from '../../types/schedule.types';

interface DayTabsProps {
  days: ScheduleDay[];
  selectedDay: string;
  onDayChange: (day: string) => void;
  primaryColor?: string;
  // New optional props to support unique day identification
  selectedDayId?: string;
  onDayChangeById?: (dayId: string, date: string) => void;
}

export const DayTabs = ({
  days,
  selectedDay,
  onDayChange,
  primaryColor = '#2563eb',
  selectedDayId,
  onDayChangeById
}: DayTabsProps) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [showLeftFade, setShowLeftFade] = useState(false);
  const [showRightFade, setShowRightFade] = useState(false);
  const [indicatorStyle, setIndicatorStyle] = useState<{ left: number; width: number } | null>(null);

  const updateFadeVisibility = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const { scrollLeft, scrollWidth, clientWidth } = container;
    const isScrollable = scrollWidth > clientWidth;
    
    // Show left fade if scrolled right and content is scrollable
    setShowLeftFade(isScrollable && scrollLeft > 0);
    
    // Show right fade if not scrolled to the end and content is scrollable
    // Adding a small threshold (1px) to account for rounding errors
    setShowRightFade(isScrollable && scrollLeft < scrollWidth - clientWidth - 1);
  };

  const updateIndicatorPosition = () => {
    const container = scrollContainerRef.current;
    if (!container) return;

    const selectedButton = container.querySelector('button[data-selected="true"]');
    if (!selectedButton) return;

    const button = selectedButton as HTMLElement;
    const containerRect = container.getBoundingClientRect();
    const buttonRect = button.getBoundingClientRect();

    setIndicatorStyle({
      left: buttonRect.left - containerRect.left + container.scrollLeft,
      width: buttonRect.width
    });
  };

  useEffect(() => {
    updateFadeVisibility();
    updateIndicatorPosition();
    
    // Update on window resize
    const handleResize = () => {
      updateFadeVisibility();
      updateIndicatorPosition();
    };
    window.addEventListener('resize', handleResize);
    
    // Use ResizeObserver if available for more precise detection
    let resizeObserver: ResizeObserver | null = null;
    if (scrollContainerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        updateFadeVisibility();
        updateIndicatorPosition();
      });
      resizeObserver.observe(scrollContainerRef.current);
    }
    
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, [days]); // Re-evaluate when days change

  // Scroll to selected day when it changes or on mount
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container) return;

    // Update indicator position
    updateIndicatorPosition();

    // Find the selected button
    const selectedButton = container.querySelector('button[data-selected="true"]');
    if (!selectedButton) return;

    // Calculate scroll position to center the selected button if possible
    const containerWidth = container.clientWidth;
    const buttonLeft = (selectedButton as HTMLElement).offsetLeft;
    const buttonWidth = (selectedButton as HTMLElement).offsetWidth;
    
    // Calculate ideal scroll position (center the button in view)
    const idealScrollLeft = buttonLeft - (containerWidth / 2) + (buttonWidth / 2);
    
    // Smooth scroll to the calculated position
    container.scrollTo({
      left: Math.max(0, idealScrollLeft),
      behavior: 'smooth'
    });

    // Update fade visibility and indicator position after scrolling
    setTimeout(() => {
      updateFadeVisibility();
      updateIndicatorPosition();
    }, 300);
  }, [selectedDay, selectedDayId]); // Re-run when selection changes

  // Update indicator position on scroll
  const handleScroll = () => {
    updateFadeVisibility();
    updateIndicatorPosition();
  };

  return <div className="border-b relative">
      {/* Left fade overlay */}
      <div 
        className={`absolute left-0 top-0 bottom-0 w-8 pointer-events-none z-10 transition-opacity duration-200 ${
          showLeftFade ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'linear-gradient(to right, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
        }}
      />
      
      {/* Right fade overlay */}
      <div 
        className={`absolute right-0 top-0 bottom-0 w-8 pointer-events-none z-10 transition-opacity duration-200 ${
          showRightFade ? 'opacity-100' : 'opacity-0'
        }`}
        style={{
          background: 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)'
        }}
      />

      <div 
        ref={scrollContainerRef}
        className="flex overflow-x-auto scrollbar-hide relative"
        onScroll={handleScroll}
      >
        {days.map((day, index) => {
          // Create a unique key using ID if available, otherwise use date + index
          const dayKey = day.id || `${day.date}-${index}`;

          // Determine if this tab is selected
          const isSelected = selectedDayId && day.id
            ? selectedDayId === day.id
            : selectedDay === day.date;

          // Handle click - use new callback if available
          const handleClick = () => {
            if (onDayChangeById && day.id) {
              onDayChangeById(day.id, day.date);
            } else {
              onDayChange(day.date);
            }
          };

          return <button
            key={dayKey}
            onClick={handleClick}
            data-selected={isSelected}
            className={`flex-shrink-0 px-6 py-3 font-medium text-sm transition-colors whitespace-nowrap
              ${isSelected ? '' : 'text-gray-500 hover:text-gray-700'}`}
            style={isSelected ? {
              color: primaryColor
            } : {}}>
            <div className="flex flex-col items-center sm:items-start">
              <span className="text-sm sm:text-base">{day.title}</span>
              <span className="text-xs text-gray-500">
                {day.subtitle}
              </span>
            </div>
          </button>
        })}
        
        {/* Animated indicator bar */}
        {indicatorStyle && (
          <div
            className="absolute bottom-0 h-0.5 transition-all duration-300 ease-out"
            style={{
              backgroundColor: primaryColor,
              left: `${indicatorStyle.left}px`,
              width: `${indicatorStyle.width}px`,
            }}
          />
        )}
      </div>
    </div>;
};